/// <amd-module name="InstantSearch/Shared/InstantSearchProviders/OtherResultsProvider" />
import * as React from "react";
import { ITfsPageService, IProjectData } from "Tfs/Platform/Page";
import { IReadonlyObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import * as Constants from "InstantSearch/Shared/Constants";
import { IVssPageContext, Services } from "VSS/Platform/Context";
import { IVssSearchService } from "VSS/Platform/Search";
import { getFriendlyDisplayValue } from "VSSUI/Utilities/FriendlyNumber";
import { IMenuItem, IMenuItemDetails, MenuCell, MenuItemType } from "VSSUI/Menu";
import * as LegacyRestClient from "Wit/Clients/SearchLegacy/RestClient/Search";
import * as SearchSharedLegacy from "Wit/Clients/SearchLegacy/Contracts/Search.SharedLegacy";
import { BaseInstantSearchProvider } from "InstantSearch/Shared/InstantSearchProviders/BaseInstantSearchProvider";
import { IExecuteSearchService } from "InstantSearch/Shared/Services/ExecuteSearchService";
import { ResultRow, IIconAndClass } from "InstantSearch/Shared/ResultRow";
import * as Resources from "InstantSearch/Shared/Resources";
import { TelemetryWriter } from "InstantSearch/Shared/TelemetryWriter";
import { IMenuServiceContext, IOthersMenuProps, IInstantSearchMenuService } from "InstantSearch/Shared/Types";

interface IEntityResultCount extends IOthersMenuProps {
    count?: number;
    showPlus?: boolean;
}

interface IMenuItemData {
    index: number;
    entity: string;
}

/**
 * Service for creating menu items for recent work items.
 */
export class OtherResultsProvider extends BaseInstantSearchProvider<SearchSharedLegacy.CountResponse[]> implements IInstantSearchMenuService<IMenuServiceContext> {
    protected _contributionId: string = "";
    private static _setHeader = true;

    private _legacySearchClient: LegacyRestClient.ISearchRestClient;
    private _otherEntities: IEntityResultCount[] = [];
    private _projectProperties: IProjectData | undefined;
    private _addHeader = false;
    /**
    * Object of telemetry writer to record telemetry data.
    */
    private _telemetryWriter: TelemetryWriter;

    private get telemetryWriter(): TelemetryWriter {
        if (!this._telemetryWriter) {
            this._telemetryWriter = new TelemetryWriter(this.pageContext, Constants.CustomerIntelligenceConstants.Feature);
        }
        return this._telemetryWriter;
    }

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        this._initContribution(this._contributionId);
        this._legacySearchClient = pageContext.getRestClient<LegacyRestClient.ISearchRestClient>("Legacy.ISearchRestClient");
    }

    protected _getMenuItems(menuId: string,
        context: IMenuServiceContext,
        invokeProvider: boolean,
        searchText: string
    ): IReadonlyObservableArray<IMenuItem> | null {
        if (invokeProvider && searchText) {
            if (this._menuItems.length === 0) {
                // update suggestions with empty results
                this._updateOtherSuggestions(context, true);
            }

            this.getSuggestions(searchText, context, "OtherSuggestionsFetched");
        } else {
            this._menuItems.splice(0, this._menuItems.length);
        }

        return this._menuItems;
    }

    protected _initContribution(contributionId: string) {
        super._initContribution(contributionId);

        // Populating other entities
        const searchService = this.pageContext.getService<IVssSearchService>("IVssSearchService");
        const selectedProviderId = searchService.getSelectedProvider();
        if (this._contribution && this._contribution.menuProps) {
            this._contribution
                .menuProps
                .forEach(menu => {
                    // Adding other entities except current hub default: i.e., for boards hub, we add other entites than 'workitem'.
                    if (menu.searchProviderId !== selectedProviderId) {

                        if (isFeatureFlagEnabled(this.pageContext, Constants.RemovePackageSearchFromInstantSearch, false)) {
                            if (menu.searchProviderId.includes("package")) {
                                // Record telemetry
                                this.telemetryWriter?.publish("PackageSearchRemoved", { selectedProviderId });

                                return;
                            }
                        }

                        this._otherEntities.push({ ...menu });

                        if (OtherResultsProvider._setHeader) {
                            OtherResultsProvider._setHeader = false;
                            this._addHeader = true;
                        }
                    }
                });
        }
    }

    protected _fetchResults(searchText: string, context: IMenuServiceContext): Promise<SearchSharedLegacy.CountResponse[]> {
        const promises = this._otherEntities.map(
            otherEntity => {
                const searchFilters: { [key: string]: string[] } = this.projectProperties ? { [otherEntity.searchProjectFilter]: [this.projectProperties.name] } : {};
                const countRequest: SearchSharedLegacy.CountRequest = { searchFilters, searchText };
                const entityType = otherEntity.entity;
                return this._legacySearchClient.resultsCount(countRequest, entityType, this.projectProperties && this.projectProperties.id);
            });

        return Promise.all(promises);
    }

    protected _onFetchSuccess(searchText: string, context: IMenuServiceContext, data: SearchSharedLegacy.CountResponse[]): void {
        const { searchInput } = context;
        if (this.shouldInvokeProvider(context) && !searchInput.queryText().trim()) {
            // Search text is empty. It got changed before result counts were fetched. So, emptying old results.
            this._menuItems.splice(0, this._menuItems.length);
        } else {
            this._otherEntities.forEach((entity, index) => {
                const response = data[index];
                const hasMoreResults = response.relationFromExactCount === SearchSharedLegacy.RelationFromExactCount.LessThanEqualTo;
                entity.count = response.count;
                entity.showPlus = hasMoreResults;
            });
            this._updateOtherSuggestions(context);
        }
    }

    protected _onFetchFail(context: IMenuServiceContext, error: Error): void {
        return; // No-op
    }

    private _updateOtherSuggestions(context: IMenuServiceContext, loadEmptyCount?: boolean): void {
        // Remove old results
        this._menuItems.splice(0, this._menuItems.length);

        if (this._addHeader) {
            // Add header
            this._menuItems.push({
                id: "",
                groupKey: this._groupKey,
                rank: this._rank,
                itemType: MenuItemType.Header,
                text: Resources.OthersHeader,
                className: "search-suggestion-header"
            });
        }

        // Add entities and count
        this._otherEntities.forEach((otherEntity, index) => {
            const { count, entity } = otherEntity;
            const title = (count !== undefined && !loadEmptyCount)
                ? format("{0} ({1})", otherEntity.label, format(otherEntity.showPlus ? "{0}+" : "{0}", getFriendlyDisplayValue(count)))
                : otherEntity.label;
            const { searchInput, telemetryWriter, onFocus } = context;
            const searchText = searchInput.queryText();
            const data: IMenuItemData = { index: index, entity: entity };
            const iconAndClass: IIconAndClass = { iconClass: otherEntity.iconClass, iconName: otherEntity.iconName };

            // Add menu items
            this._menuItems.push({
                id: "",
                groupKey: this._groupKey,
                rank: this._rank + otherEntity.position,
                renderMenuCell: (menuCell: MenuCell, menuItem: IMenuItem, details: IMenuItemDetails): JSX.Element | undefined =>
                    this._renderMenuCell(menuCell, menuItem, details, iconAndClass, title, onFocus),
                onActivate: (menuItem: IMenuItem, event: React.KeyboardEvent<HTMLElement>) =>
                    this._executeSearch(
                        searchText,
                        otherEntity.searchProviderId,
                        otherEntity.entity,
                        event.ctrlKey,
                        () => this._onOtherResultClick(data, searchInput.setHelpVisibility, telemetryWriter))
            });
        });
    }

    private _executeSearch = (queryText: string, searchProviderId?: string, entityType?: string, openInNewWindow?: boolean, callbackBeforeExecute?: () => void): void => {
        const executeSearchService = this.pageContext.getService<IExecuteSearchService>("IExecuteSearchService");
        executeSearchService.executeSearch(queryText, searchProviderId, entityType, openInNewWindow, callbackBeforeExecute);
    }

    private _renderMenuCell = (
        menuCell: MenuCell,
        menuItem: IMenuItem,
        details: IMenuItemDetails,
        iconAndClass: IIconAndClass,
        title: string,
        onFocus: (index: number, event: React.FocusEvent<HTMLElement>) => void
    ): JSX.Element | undefined => {
        details.onFocusItem = onFocus;
        switch (menuCell) {
            case MenuCell.PrimaryText:
                return <ResultRow iconProps={iconAndClass} title={title} />;
            default:
                return undefined;
        }
    }

    private _onOtherResultClick = (data: IMenuItemData, setHelpVisibility: (hideHelp: boolean, focusInputOnShow?: boolean) => void, telemetryWriter: TelemetryWriter): void => {
        // Hide callout
        setHelpVisibility(true);

        // Record telemetry
        telemetryWriter.publish("ResultClicked", {
            "index": data.index,
            "resultType": "OtherResults",
            "entityType": data.entity
        });
    }

    private get projectProperties(): IProjectData | undefined {
        if (!this._projectProperties) {
            const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
            const tfsPageData = tfsPageService.getData();
            this._projectProperties = tfsPageData ? tfsPageData.project : undefined;
        }

        return this._projectProperties;
    }
}

function getOtherResultsProvider(contributionId: string): typeof OtherResultsProvider {
    return class extends OtherResultsProvider {
        protected _contributionId = contributionId;
    }
}

Services.add(
    "code-other-results-project-provider-service",
    { serviceFactory: getOtherResultsProvider("ms.vss-code-web.code-other-results-project-menu-provider") }
);

Services.add(
    "workitem-other-results-project-provider-service",
    { serviceFactory: getOtherResultsProvider("ms.vss-work-web.workitem-other-results-project-menu-provider") }
);

Services.add(
    "wiki-other-results-project-provider-service",
    { serviceFactory: getOtherResultsProvider("ms.vss-wiki-web.wiki-other-results-project-menu-provider") }
);

Services.add(
    "package-other-results-project-provider-service",
    { serviceFactory: getOtherResultsProvider("ms.feed.package-other-results-project-menu-provider") }
);

Services.add(
    "code-other-results-collection-provider-service",
    { serviceFactory: getOtherResultsProvider("ms.vss-code-web.code-other-results-collection-menu-provider") }
);

Services.add(
    "workitem-other-results-collection-provider-service",
    { serviceFactory: getOtherResultsProvider("ms.vss-work-web.workitem-other-results-collection-menu-provider") }
);

Services.add(
    "wiki-other-results-collection-provider-service",
    { serviceFactory: getOtherResultsProvider("ms.vss-wiki-web.wiki-other-results-collection-menu-provider") }
);

Services.add(
    "package-other-results-collection-provider-service",
    { serviceFactory: getOtherResultsProvider("ms.feed.package-other-results-collection-menu-provider") }
);
